import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReservationRow, ReservationType, RescheduleRequest } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { QuoteBreakdown } from './QuoteBreakdown';
import { CZK, TYPE_LABEL } from '../lib/format';

/**
 * "Změnit termín" — the legacy edit form's dates, product and price on a
 * booking that exists. The price is quoted live for the new term on the
 * booking's own pricelist, the way the legacy page re-asked getprice as
 * the dates changed; the desk can keep the old price or type its own.
 */
const kc = (n: number) => `${CZK.format(n)} Kč`;
const split = (v: string) => ({ d: v.slice(0, 10), t: v.slice(11, 16) });

export function RescheduleForm({ r, onClose }: { r: ReservationRow; onClose: () => void }) {
  const qc = useQueryClient();
  const a0 = split(r.arrival);
  const d0 = split(r.departure);
  const [arrD, setArrD] = useState(a0.d);
  const [arrT, setArrT] = useState(a0.t);
  const [depD, setDepD] = useState(d0.d);
  const [depT, setDepT] = useState(d0.t);
  const [type, setType] = useState<ReservationType>(r.type);
  /* The price is a plain field, prefilled with the booking's price, as the
     old edit form had it. A quote for a changed term is shown beside it and
     taken over with one click ("podle ceníku"), never silently: the
     manager's test booking (22 October 2026) went from his 1 Kč to 2 350 Kč
     on a date change because the old drop-down defaulted to the quote. What
     the field says when Uložit is pressed is what the booking gets. */
  const [own, setOwn] = useState(String(r.price));
  const [note, setNote] = useState('');
  const arrived = !!r.arrivalReal;

  const arrival = `${arrD} ${arrT}`;
  const departure = `${depD} ${depT}`;
  const datesOk = !!(arrD && arrT && depD && depT) && departure > arrival;
  const [key, setKey] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setKey(`${arrival}|${departure}|${type}`), 250);
    return () => clearTimeout(t);
  }, [arrival, departure, type]);
  const q = useQuery({
    queryKey: ['quote', 'resched', r.id, key],
    queryFn: () => api.quote({ arrival, departure, type, pricelistId: r.pricelistId, dynamic: true }),
    enabled: datesOk && key !== '',
    retry: false,
  });

  const m = useMutation({
    mutationFn: () => {
      const body: RescheduleRequest = { note: note.trim() };
      if (arrival !== r.arrival.slice(0, 16)) body.arrival = arrival;
      if (departure !== r.departure.slice(0, 16)) body.departure = departure;
      if (type !== r.type) body.type = type;
      if (Number(own) !== r.price) body.price = Number(own);
      else body.requote = false;
      return api.reschedule(r.id, body);
    },
    onSuccess: async () => {
      await qc.invalidateQueries();
      onClose();
    },
  });
  const err = m.error;
  const message =
    err instanceof ApiError
      ? err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message)
      : err instanceof Error
        ? err.message
        : null;
  const termChanged = arrival !== r.arrival.slice(0, 16) || departure !== r.departure.slice(0, 16) || type !== r.type;
  const changed = termChanged || Number(own) !== r.price;
  const ownOk = Number.isInteger(Number(own)) && Number(own) >= 0 && Number(own) <= 65535;

  return (
    <form
      className="card detailcard editform"
      onSubmit={(e) => {
        e.preventDefault();
        if (!m.isPending && datesOk && changed && ownOk) m.mutate();
      }}
    >
      <h2>Změnit termín</h2>
      <div className="efgrid">
        <label className="ef">
          Produkt
          <select value={type} onChange={(e) => setType(e.target.value as ReservationType)}>
            {(['cheap', 'regular', 'exclusive'] as const).map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="ef">
          Příjezd{arrived ? ' (vůz už přijel)' : ''}
          <span className="dt">
            <input type="date" value={arrD} onChange={(e) => setArrD(e.target.value)} disabled={arrived} required />
            <input type="time" value={arrT} onChange={(e) => setArrT(e.target.value)} disabled={arrived} required />
          </span>
        </label>
        <label className="ef">
          Odjezd
          <span className="dt">
            <input type="date" value={depD} onChange={(e) => setDepD(e.target.value)} required autoFocus />
            <input type="time" value={depT} onChange={(e) => setDepT(e.target.value)} required />
          </span>
        </label>
        <label className="ef">
          Cena (Kč)
          <input type="number" min={0} max={65535} step={1} value={own} onChange={(e) => setOwn(e.target.value)} />
          {/* The manager's re-test (26 October 2026): he changed the departure
              and expected the price due to follow. It does not, by the owner's
              ruling of 22 October (a price changes only on purpose) — so the
              form says so, with the list price beside it. */}
          {termChanged && q.data && Number(own) === r.price && q.data.price !== r.price && (
            <span className="pricehint" role="status">
              Cena se sama nemění: zůstává {kc(r.price)}. Podle ceníku by byla <b>{kc(q.data.price)}</b> (
              {q.data.price > r.price ? `o ${kc(q.data.price - r.price)} víc` : `o ${kc(r.price - q.data.price)} méně`}) — tlačítko „podle ceníku“.
            </span>
          )}
        </label>
        <label className="ef wide">
          Důvod (do historie)
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} placeholder="nepovinné" />
        </label>
      </div>
      <div className="quotebox" aria-live="polite">
        {!datesOk ? (
          <span className="empty">Odjezd musí být po příjezdu.</span>
        ) : q.isPending ? (
          <span className="empty">Počítám cenu…</span>
        ) : q.data ? (
          <span>
            <b>{q.data.days}</b> {q.data.days === 1 ? 'den' : q.data.days < 5 ? 'dny' : 'dní'} · ceník #{q.data.pricelistId}{' '}
            {q.data.pricelistTitle} · <b>{kc(q.data.price)}</b>
            {q.data.factor !== 1 && <span className="was"> = {kc(q.data.base)} × {q.data.factor}</span>}
            {' '}
            <span className="was">(nyní {kc(r.price)})</span>
            {Number(own) !== q.data.price && (
              <>
                {' '}
                <button type="button" className="btn sm" onClick={() => setOwn(String(q.data!.price))}>
                  podle ceníku
                </button>
              </>
            )}
            {q.data.v2 && <QuoteBreakdown q={q.data} />}
            {(q.data.closed.arrival || q.data.closed.departure) && (
              <span className="arrive-error" role="alert">
                {' '}
                Uzavírka: {q.data.closed.arrival && q.data.closed.departure ? 'příjezdy i odjezdy' : q.data.closed.arrival ? 'příjezdy' : 'odjezdy'} v ten den zavřené.
              </span>
            )}
          </span>
        ) : (
          <span className="arrive-error">Cenu se nepodařilo spočítat.</span>
        )}
      </div>
      <div className="efactions">
        <button type="submit" className="btn primary" disabled={m.isPending || !datesOk || !changed || !ownOk}>
          {m.isPending ? 'Ukládám…' : 'Uložit změnu'}
        </button>
        <button type="button" className="btn" onClick={onClose} disabled={m.isPending}>
          Zrušit
        </button>
        {message && (
          <span className="arrive-error" role="alert">
            {message}
          </span>
        )}
      </div>
    </form>
  );
}
